import { Alert, Box, Button, TextField } from "@mui/material";
import * as React from "react";
import { useDispatch } from "react-redux";
import { useHotkeys } from "react-hotkeys-hook";
import classNames from "../../../helpers/classNames";
import useMobileScreen from "../../../hooks/useMobileScreen";
import { BillItemType, StockUnitTypes } from "../../../interfaces/BillInterfaces";
import { StockProducts } from "../../../interfaces/StockInterfaces";
import BatchInfoPopup from "./BatchInfoPopup";
import { addNewRow } from "../../../actions/bill";

// @ts-expect-error - css
import styles from "./BillEditor.module.css";

interface BillQuantityProps {
  item: BillItemType;
  column: number;
  isCreditNote: boolean;
  batchOptions: Array<StockProducts>;
  showBatchPopup: boolean;
  setShowBatchPopup: (option: boolean) => void;
  updateBillItem: (billItem, batchOptions?) => void;
  selectedBatchAvailableQuantity: number;
  selectedStockProducts: Array<StockProducts>;
  setSelectedStockProducts: (stockProducts) => void;
  notEnoughQuantityMsg: string;
  setNotEnoughQuantityMsg: (message) => void;
  infoText: string;
  setInfoText: (value: string) => void;
  quantityRef: HTMLInputElement;
  creditableQuantity: number;
}

const BillQuantity: React.FC<BillQuantityProps> = ({
  item,
  column,
  isCreditNote,
  batchOptions,
  showBatchPopup,
  setShowBatchPopup,
  updateBillItem,
  selectedBatchAvailableQuantity,
  selectedStockProducts,
  setSelectedStockProducts,
  notEnoughQuantityMsg,
  setNotEnoughQuantityMsg,
  infoText,
  setInfoText,
  quantityRef,
  creditableQuantity
}) => {
  const isMobileScreen = useMobileScreen();
  const isQuantityFocused = () => quantityRef?.current === document.activeElement;
  const billedItemRef = React.useRef(item);

  const dispatch = useDispatch();
  useHotkeys(
    "enter",
    () => {
      if (isQuantityFocused()) {
        dispatch(addNewRow());
      }
      setShowBatchPopup(false);
    },
    { enableOnTags: ["INPUT", "SELECT", "TEXTAREA"] }
  );

  return (
    <Box>
      <BatchInfoPopup
        infoText={infoText}
        setInfoText={setInfoText}
        updateBillItem={updateBillItem}
        showBatchPopup={showBatchPopup}
        setShowBatchPopup={setShowBatchPopup}
        batchOptions={batchOptions}
        quantityRef={quantityRef}
        selectedStockProducts={selectedStockProducts}
        item={item}
        handleSwitchBatch={(batch) => {
          updateBillItem(
            {
              ...item,
              quantity: 1,
              batchInfo: { batchId: batch.batchId, quantity: 1, expiryDate: batch.expiryDate },
              perUnit: Number(batch.unitPriceExcVAT)
            },
            batchOptions
          );
          setSelectedStockProducts((prevState) =>
            prevState.map((stock) => {
              if (stock.batchId === item.batchInfo.batchId) {
                return batch;
              }
              return stock;
            })
          );
        }}
      />

      {(notEnoughQuantityMsg || item.showAdjustAvailableQtyMsg) && (
        <Alert
          className={classNames(styles.alertRoot, { [styles.mobileRoot]: isMobileScreen })}
          severity="warning"
          action={
            <Button
              onClick={() => {
                setNotEnoughQuantityMsg("");
                updateBillItem({ ...item, showAdjustAvailableQtyMsg: false });
              }}
              variant="outlined"
              color="inherit"
              size="small"
            >
              Got it!
            </Button>
          }
        >
          {notEnoughQuantityMsg ||
            `This stock quantity has been updated to match the available quantity.`}
        </Alert>
      )}

      <TextField
        data-testmation="billQuantityInput"
        inputRef={quantityRef}
        type="number"
        value={item[column]}
        disabled={
          isCreditNote
            ? !item.returned || (creditableQuantity !== undefined && creditableQuantity <= 0)
            : false
        }
        slotProps={{
          input: {
            classes: { input: styles.rowInputRoot }
          }
        }}
        margin="none"
        variant="outlined"
        onFocus={(e) => {
          e.target.select();
          if (isCreditNote) return;
          if (item?.batchInfo?.batchId && batchOptions?.length) {
            setShowBatchPopup(true);
          }
          if (batchOptions?.length === 0) {
            setNotEnoughQuantityMsg("This product is currently out of stock.");
          }
        }}
        onChange={(e) => {
          let quantity = Number(e.target.value);

          if (!isCreditNote) {
            if (batchOptions?.length === 0) {
              quantity = 0;
              setNotEnoughQuantityMsg("This product is currently out of stock.");
            }
            if (selectedBatchAvailableQuantity && selectedBatchAvailableQuantity < quantity) {
              quantity = selectedBatchAvailableQuantity;
              setNotEnoughQuantityMsg("Selected batch does not have enough quantity.");
            }
          }

          if (isCreditNote) {
            const intendedQuantityInUnit =
              item.stockUnitType === StockUnitTypes.unit
                ? quantity
                : (billedItemRef.current.stockProductUnitsPerPackage || 1) * quantity;

            const availableQuantityInUnit = creditableQuantity || 0;

            if (Math.abs(intendedQuantityInUnit) > Math.abs(availableQuantityInUnit)) {
              setNotEnoughQuantityMsg("Cannot return more quantity than maximum creditable.");
              quantity =
                item.stockUnitType === StockUnitTypes.unit
                  ? availableQuantityInUnit
                  : Math.floor(
                      availableQuantityInUnit /
                        (billedItemRef.current.stockProductUnitsPerPackage || 1)
                    );
            }
          }

          // setShowBatchPopup(true);
          updateBillItem({
            ...item,
            quantity,
            calculationBasis: "perUnit",
            batchInfo: { ...item.batchInfo, quantity }
          });
          if (item.batchInfo.batchId && batchOptions?.length) setShowBatchPopup(true);
          else setShowBatchPopup(false);
        }}
      />
    </Box>
  );
};

export default BillQuantity;
